import { lazy, PropsWithChildren, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { usePrevious } from 'react-use';

import { ROUTE_PATHS } from '@/shared/consts/routes';

import { IUserRole } from 'api/apiUsers/models';
import { useTypedSelector } from 'store';
import { SessionLogoutModal } from 'ui/SessionLogoutModal';
import { Spinner } from 'ui/Spinner/Spinner';
import { ApplicationLayout } from 'widgets/application-layout';

const Pair = lazy(() => import('pages/pair'));
const Project = lazy(() => import('pages/Project/Project'));
const Wallets = lazy(() => import('pages/Wallets/Wallets'));
const Accounts = lazy(() => import('pages/Account/Accounts'));
const Reports = lazy(() => import('pages/Reports/Reports'));
const Authentication = lazy(() => import('pages/Authentication/Authentication'));
const Arbitrage = lazy(() => import('pages/Arbitrage/Arbitrage'));
const Balancer = lazy(() => import('pages/Balancer/Balancer'));
const CexPair = lazy(() => import('pages/CexPair'));
const DexPair = lazy(() => import('pages/DexPair/DexPair'));
const SolPair = lazy(() => import('pages/SolPair/SolPair'));

type PrivateRouteProps = {
  roles: IUserRole[];
} & PropsWithChildren;

const suspenseFallback = (
  <div className="flex items-center justify-center p-6">
    <Spinner size="medium" />
  </div>
);

function PrivateRoute({ children, roles }: PrivateRouteProps) {
  const user = useTypedSelector(store => store.auth.user);

  if (!user) return <Navigate to={ROUTE_PATHS.login} />;

  if (user && !roles.includes(user.role)) {
    return <Navigate to={'/'} />;
  }

  return <>{children}</>;
}

export function Routing() {
  const location = useLocation();
  const userIsAuthenticated = useTypedSelector(store => Boolean(store.auth.jwt));
  const userRole = useTypedSelector(store => store.auth.user?.role);

  const prevPathname = usePrevious(location.pathname);

  // Theme is automatically initialized from Zustand store (persisted in localStorage)

  if (!userIsAuthenticated) {
    return (
      <Suspense fallback={suspenseFallback}>
        <Routes>
          <Route
            path={ROUTE_PATHS.login}
            element={<Authentication prevPathname={prevPathname} />}
          />
          <Route path="*" element={<Navigate to={ROUTE_PATHS.login} replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <ApplicationLayout>
      <Suspense fallback={suspenseFallback}>
        <SessionLogoutModal />

        <Routes>
          <Route
            path={ROUTE_PATHS.wallets}
            element={
              <PrivateRoute roles={['admin']}>
                <Wallets />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.accounts}
            element={
              <PrivateRoute roles={['admin']}>
                <Accounts />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.reports}
            element={
              <PrivateRoute roles={['admin']}>
                <Reports />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.balancer}
            element={
              <PrivateRoute roles={['admin']}>
                <Balancer />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.balancerCreate}
            element={
              <PrivateRoute roles={['admin']}>
                <Balancer />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.project}
            element={
              <PrivateRoute roles={['admin', 'user']}>
                <Project />
              </PrivateRoute>
            }
          />

          <Route
            path={ROUTE_PATHS.pair}
            element={
              <PrivateRoute roles={['admin']}>
                <Pair />
              </PrivateRoute>
            }
          />

          <Route path={ROUTE_PATHS.dexPair} element={<DexPair />} />
          <Route path={ROUTE_PATHS.solPair} element={<SolPair />} />
          <Route path={ROUTE_PATHS.cexPair} element={<CexPair />} />

          <Route
            path={ROUTE_PATHS.arbitrage}
            element={
              <PrivateRoute roles={['admin']}>
                <Arbitrage />
              </PrivateRoute>
            }
          />

          {userRole === 'admin' ? (
            <Route path="*" element={<Navigate to={ROUTE_PATHS.wallets} replace />} />
          ) : (
            <>
              <Route index element={<></>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </Suspense>
    </ApplicationLayout>
  );
}
